import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
// AFTER (Correct)
import { getBankDetail, getChargesForBank } from '../../services/dataService';
import { BankDetail, Charge } from '../../types';
import LoadingSpinner from '../../components/LoadingSpinner';
import Breadcrumbs from '../../components/Breadcrumbs';
import ShareButton from '../../components/ShareButton';
import AdComponent from '../../components/AdComponent';

const BankChargeDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [bank, setBank] = useState<BankDetail | null>(null);
    const [charges, setCharges] = useState<Charge[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (slug) {
            setLoading(true);
            // Reset state on slug change
            setBank(null);
            setCharges([]);
            getBankDetail(slug).then(bankData => {
                setBank(bankData || null);
                if (bankData) {
                    // Fetch detailed charges for the bank
                    getChargesForBank(bankData.id).then(chargeData => {
                        setCharges(chargeData);
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            });
        }
    }, [slug]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!bank) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold">Bank not found</h2>
                <Link to="/bank-charges" className="text-[#246BFD] hover:underline mt-4 inline-block">
                    &larr; Back to all banks
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 py-12">
            <div className="container mx-auto px-6 max-w-4xl">
                <Breadcrumbs />
                <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                    <div>
                        <h1 className="text-4xl font-extrabold font-poppins tracking-tight text-gray-900">{bank.name}</h1>
                        <p className="text-lg text-gray-600">{bank.category}</p>
                    </div>
                    <ShareButton
                        title={`All about ${bank.name} charges | ChargesDecoder`}
                        text={`Find all charge details for ${bank.name} on ChargesDecoder.`}
                        className="mt-4 sm:mt-0"
                    />
                </header>

                <main>
                    <AdComponent />
                    <h2 className="text-2xl font-bold font-poppins text-gray-800 mb-6">Common Charges</h2>
                    <div className="bg-white rounded-lg shadow-md border border-gray-200">
                        <ul className="divide-y divide-gray-200">
                            {charges.map(charge => (
                                <li key={charge.id}>
                                    <Link to={`/charges/${charge.id}`} className="block p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
                                        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-800">{charge.fullName} <span className="text-gray-500 font-normal">({charge.name})</span></h3>
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{charge.whatItMeans}</p>
                                            </div>
                                            <div className="flex flex-row items-center gap-4 flex-shrink-0 self-start sm:self-center">
                                                <p className="font-semibold text-[#246BFD] text-lg font-roboto-mono text-left sm:text-right">{charge.typicalAmount}</p>
                                                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                         {charges.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                                No specific charges listed for this bank in our main database yet.
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default BankChargeDetailPage;
